import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, ChevronDown, Plus, X, Eye, ShoppingCart, ShieldCheck } from 'lucide-react';

const ProfileSelector = ({ 
  profiles, 
  currentProfile, 
  onSelectProfile, 
  onAddProfile,
  onUpdateProfile,
  onDeleteProfile,
  onPurchaseMore,
  maxProfiles = 3,
  ninjaLimits = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProfileData, setNewProfileData] = useState({
    name: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    birth: '',
    occupation: [],
    status: ''
  });
  const [editingId, setEditingId] = useState(null);

  const occupations = [
    'empleado/a', 'emprendedor/a', 'ventas', 'estudiante', 'hogar',
    'creador/a', 'artista', 'diseñador/a', 'escritor/a', 'músico/a',
    'actor/actriz', 'fotógrafo/a', 'profesional independiente',
    'gerente/directivo', 'consultor/a', 'coach/mentor', 'terapeuta',
    'médico/a', 'abogado/a', 'ingeniero/a', 'arquitecto/a',
    'docente', 'investigador/a', 'otro'
  ];

  const statusOptions = [
    'soltero/a', 'en relación', 'casado/a', 'divorciado/a', 'viudo/a'
  ];

  const handleInputChange = (field, value) => {
    setNewProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleOccupationToggle = (occupation) => {
    setNewProfileData(prev => {
      const current = prev.occupation;
      if (current.includes(occupation)) {
        return { ...prev, occupation: current.filter(o => o !== occupation) };
      } else if (current.length < 2) {
        return { ...prev, occupation: [...current, occupation] };
      }
      return prev;
    });
  };

  const handleAddOrUpdateProfile = () => {
    if (!newProfileData.name || !newProfileData.birth) {
      alert('Por favor completa al menos el nombre y fecha de nacimiento');
      return;
    }

    // Asegurar que occupation sea array
    const profileToAdd = {
      ...newProfileData,
      occupation: newProfileData.occupation.length > 0
        ? newProfileData.occupation
        : ['otro']
    };

    if (editingId) {
      onUpdateProfile({ id: editingId, ...profileToAdd });
    } else {
      // Delegar al padre (App) para crear vía API y luego actualizar lista
      onAddProfile(profileToAdd);
    }
    
    // Resetear form
    setNewProfileData({
      name: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      birth: '',
      occupation: [],
      status: ''
    });
    setEditingId(null);
    setShowAddModal(false);
  };

  const ninjaCount = profiles.filter(profile => profile.isNinja).length;
  const normalizedMax = typeof maxProfiles === 'number' && maxProfiles >= 0 ? maxProfiles : Infinity;
  const canAddMore = ninjaCount < normalizedMax;
  const remainingSlots = normalizedMax === Infinity ? Infinity : normalizedMax - ninjaCount;

  return (
    <>
      {/* Selector dropdown */}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white border-2 border-morado rounded-xl hover:bg-morado/5 transition-all shadow-sm hover:shadow-md"
        >
          {currentProfile.isNinja ? (
            <Eye className="w-4 h-4 md:w-5 md:h-5 text-morado" />
          ) : (
            <User className="w-4 h-4 md:w-5 md:h-5 text-morado" />
          )}
          <span className="font-semibold text-morado text-sm md:text-base truncate max-w-[100px] md:max-w-none">
            {currentProfile.name}
            {currentProfile.isNinja && ' 🥷'}
          </span>
          <ChevronDown className={`w-3 h-3 md:w-4 md:h-4 text-morado transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 mt-2 w-64 bg-white border-2 border-morado rounded-xl shadow-xl z-50 overflow-hidden"
            >
              {/* Lista de perfiles */}
              <div className="max-h-64 overflow-y-auto">
                {profiles.map((profile) => (
                  <button
                    key={profile.id}
                    onClick={() => {
                      onSelectProfile(profile);
                      setIsOpen(false);
                    }}
                    className={`w-full px-4 py-3 flex items-center gap-2 hover:bg-morado/10 transition-colors ${
                      profile.id === currentProfile.id ? 'bg-morado/5' : ''
                    }`}
                  >
                    {profile.isNinja ? (
                      <Eye className="w-4 h-4 text-morado" />
                    ) : (
                      <User className="w-4 h-4 text-morado" />
                    )}
                    <span className="text-sm font-medium text-gray-800">
                      {profile.name}
                      {profile.isNinja && ' 🥷'}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      {profile.id === currentProfile.id && (
                        <span className="text-xs text-morado">✓</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Botón agregar perfil */}
              {canAddMore && (
                <div className="border-t-2 border-morado/20">
                  <button
                    onClick={() => {
                      setShowAddModal(true);
                      setIsOpen(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-2 bg-morado/5 hover:bg-morado/10 transition-colors"
                  >
                    <Plus className="w-4 h-4 text-morado" />
                    <span className="text-sm font-semibold text-morado">
                      {editingId ? 'Editar Perfil Ninja' : 'Agregar Perfil Ninja 🥷'}
                    </span>
                  </button>
                  <div className="px-4 py-2 bg-morado/5 text-xs text-gray-600 text-center">
                    {remainingSlots === Infinity
                      ? 'Puedes crear perfiles Ninja ilimitados'
                      : remainingSlots === 1
                        ? 'Te queda 1 perfil Ninja disponible'
                        : `Te quedan ${remainingSlots} perfiles Ninja disponibles`
                    }
                  </div>
                </div>
              )}

              {!canAddMore && (
                <div className="px-4 py-3 border-t-2 border-morado/20 bg-[#fef2f2]">
                  <p className="text-xs text-red-600 text-center">
                    Has alcanzado el límite de perfiles Ninja permitidos por tu plan.
                  </p>
                </div>
              )}

              {/* Botón Comprar Perfiles Adicionales - SIEMPRE visible */}
              {onPurchaseMore && (
                <div className="border-t-2 border-morado/20">
                  <button
                    onClick={() => {
                      onPurchaseMore();
                      setIsOpen(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-2 bg-morado/10 hover:bg-morado/20 transition-colors"
                  >
                    <ShoppingCart className="w-4 h-4 text-morado" />
                    <span className="text-sm font-semibold text-morado">
                      Comprar Perfiles Adicionales (Ninja)
                    </span>
                  </button>
                  {ninjaLimits && (
                    <div className="px-4 py-2 bg-morado/5 text-xs text-gray-600 text-center">
                      Base: {ninjaLimits.base || 0} | 
                      Agregados: {ninjaLimits.manuallyAdded || 0} | 
                      Comprados: {ninjaLimits.purchased || 0} | 
                      Total: {ninjaLimits.total || 0}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modal para agregar perfil */}
      {showAddModal && createPortal(
        <AnimatePresence>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-[9999] p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full my-8"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-morado flex items-center gap-2">
                    <Eye className="w-6 h-6 text-morado" />
                    Agregar Perfil Ninja
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Ver la numerología de alguien más sin que lo sepa
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-morado/20 bg-morado/5 px-3 py-3 text-xs text-morado">
                    <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p className="leading-relaxed">
                      Por respeto a cada suscripción, los datos de los perfiles Ninja no pueden editarse dentro de la app.
                      Asegúrate de capturarlos correctamente; si requieres un ajuste especial, escríbenos a{" "}
                      <a href="mailto:info@numerologia-cotidiana.com" className="font-semibold underline decoration-dotted">
                        info@numerologia-cotidiana.com
                      </a>{" "}
                      y con gusto te ayudamos.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={newProfileData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                      placeholder="Nombre de la persona"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Nacimiento *
                    </label>
                    <input
                      type="date"
                      value={newProfileData.birth}
                      onChange={(e) => handleInputChange('birth', e.target.value)}
                      className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Apellido Paterno
                    </label>
                    <input
                      type="text"
                      value={newProfileData.apellidoPaterno}
                      onChange={(e) => handleInputChange('apellidoPaterno', e.target.value)}
                      className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                      placeholder="Opcional"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Apellido Materno
                    </label>
                    <input
                      type="text"
                      value={newProfileData.apellidoMaterno}
                      onChange={(e) => handleInputChange('apellidoMaterno', e.target.value)}
                      className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-morado focus:border-transparent"
                      placeholder="Opcional"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ocupación (hasta 2)
                  </label>
                  <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3">
                    {occupations.map((occ) => {
                      const isSelected = newProfileData.occupation.includes(occ);
                      const isDisabled = !isSelected && newProfileData.occupation.length >= 2;
                      
                      return (
                        <button
                          key={occ}
                          onClick={() => handleOccupationToggle(occ)}
                          disabled={isDisabled}
                          className={`px-2 py-1 text-xs rounded-lg transition ${
                            isSelected
                              ? 'bg-morado text-white'
                              : isDisabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {occ}
                          {isSelected && ' ✓'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado Civil
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {statusOptions.map((status) => (
                      <button
                        key={status}
                        onClick={() => handleInputChange('status', status)}
                        className={`px-3 py-2 text-sm rounded-lg transition ${
                          newProfileData.status === status
                            ? 'bg-morado text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddOrUpdateProfile}
                  className="flex-1 px-4 py-3 bg-morado text-white rounded-xl hover:shadow-lg transition"
                >
                  {editingId ? 'Guardar Cambios' : 'Agregar Perfil 🥷'}
                </button>
              </div>
            </motion.div>
          </div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default ProfileSelector;

